import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Complaint } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconFlag } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;

// Complaint workflow status → label + tone. 0 is the comms-svc default
// (new/open); the back-office advances it. Unknown ids fall back to the number.
// Keep the id↔meaning mapping in sync with admin-svc's complaintStatusName
// (handlers/complaint_status.go) — same id space, owned by comms-svc.
const STATUS: Record<number, { label: string; tone: BadgeTone }> = {
  0: { label: 'Open', tone: 'warn' },
  1: { label: 'In review', tone: 'info' },
  2: { label: 'Resolved', tone: 'ok' },
  3: { label: 'Closed', tone: 'neutral' },
};

// The transitions the drawer offers, in display order. Driven off STATUS so a
// new workflow state only needs adding above.
const STATUS_OPTIONS: number[] = [0, 1, 2, 3];

function statusBadge(status: number) {
  const s = STATUS[status] ?? { label: `#${status}`, tone: 'neutral' as BadgeTone };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

function statusLabel(status: number): string {
  return STATUS[status]?.label ?? `#${status}`;
}

/** Complaints — list/search the comms-svc `complaints` table (rider complaints
 *  about a trip). DEFENSIVE: shows a clean "not yet available" state when the
 *  table isn't in this Postgres. The drawer carries a status-update control
 *  (gated by complaints.manage) that proxies comms-svc. Covers the Filament
 *  ComplaintResource list/view/edit-status. */
export function Complaints() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Complaint | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'complaints', q, offset],
    queryFn: ({ signal }) => adminApi.complaints({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Complaint>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'subject', header: 'Subject', cell: (r) => <span className="font-medium text-slate-100">{r.subject || '—'}</span> },
    { key: 'trip', header: 'Trip', cell: (r) => <span className="metric text-xs text-slate-300">#{r.trip_id}</span> },
    { key: 'cust', header: 'Customer', cell: (r) => <span className="metric text-xs text-slate-300">#{r.customer_id}</span> },
    { key: 'drv', header: 'Driver', cell: (r) => <span className="metric text-xs text-slate-300">{r.driver_id ? `#${r.driver_id}` : '—'}</span> },
    { key: 'status', header: 'Status', cell: (r) => statusBadge(r.status) },
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Complaints"
        subtitle="Rider complaints — search by subject or description. Open one to update its status."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search complaints…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Complaints not yet in Postgres"
            description={data?.note ?? 'The complaints table is owned by comms-svc and is not in this Postgres yet. This page lights up automatically once it lands.'}
            icon={<IconFlag className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading complaints…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load complaints" description={(error as Error)?.message} />
          ) : (
            <>
              <DataTable<Complaint>
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(r) => String(r.id)}
                stickyHeader
                empty={<EmptyState title="No complaints" description="No complaints match this search." icon={<IconFlag className="h-6 w-6" />} />}
              />
              <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
            </>
          )}
        </Card>
      )}

      {selected ? <ComplaintDrawer complaint={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** ComplaintDrawer — the full complaint detail + the status-update control. The
 *  list already carries every column, so no extra fetch is needed; the locally
 *  tracked status reflects an update immediately while the list query refetches
 *  in the background so its badge stays in sync. */
function ComplaintDrawer({ complaint: c, onClose }: { complaint: Complaint; onClose: () => void }) {
  // Track the live status locally so the header badge + control reflect a
  // successful update without waiting for the list refetch to round-trip.
  const [status, setStatus] = useState<number>(c.status);

  return (
    <Drawer title={c.subject || `Complaint #${c.id}`} subtitle={`#${c.id} · trip #${c.trip_id}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Field label="ID" value={`#${c.id}`} />
          <Field label="Status" value={statusBadge(status)} />
          <Field label="Trip" value={`#${c.trip_id}`} />
          <Field label="Customer" value={`#${c.customer_id}`} />
          <Field label="Driver" value={c.driver_id ? `#${c.driver_id}` : 'unresolved'} />
          <Field label="Category" value={`#${c.complaint_category} / #${c.complaint_sub_category}`} />
          <Field label="Filed" value={dateTime(c.created_at)} />
        </div>

        <ComplaintStatusControl id={c.id} status={status} onUpdated={setStatus} />

        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Description</div>
          <p className="whitespace-pre-wrap text-sm text-slate-200">{c.description || 'No description provided.'}</p>
        </Card>
      </div>
    </Drawer>
  );
}

/** ComplaintStatusControl — the gated complaint-status write. Picks a new
 *  workflow status (+ optional note recorded in the audit trail) and posts to
 *  admin-svc, which proxies comms-svc's internal status endpoint (comms-svc owns
 *  the `complaints` table). On success it lifts the new status to the drawer and
 *  invalidates the complaints list so the table badge updates.
 *
 *  RBAC: only ops + superadmin hold complaints.manage, so other roles see a
 *  read-only note instead of the control. (admin-svc enforces the same matrix
 *  and 403s regardless — this is a UX affordance, not the security boundary.) */
function ComplaintStatusControl({
  id,
  status,
  onUpdated,
}: {
  id: number;
  status: number;
  onUpdated: (status: number) => void;
}) {
  const canManage = useAdminPermission('complaints.manage');
  const qc = useQueryClient();
  // The status the staffer has selected to move to (defaults to current).
  const [next, setNext] = useState<number>(status);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: () => adminApi.updateComplaintStatus(id, next, note),
    onSuccess: (res) => {
      onUpdated(res.new_status);
      setNote('');
      // Refresh the list so its status badge reflects the change.
      qc.invalidateQueries({ queryKey: ['admin', 'complaints'] });
    },
  });

  if (!canManage) {
    return (
      <Card>
        <p className="text-xs text-muted">
          Read-only — your role can review complaints but cannot change their status.
          (That action requires the <code className="text-slate-400">ops</code> role.)
        </p>
      </Card>
    );
  }

  const dirty = next !== status;

  return (
    <Card>
      <div className="mb-2 text-sm font-medium text-slate-100">Update status</div>
      <p className="mb-3 text-xs text-muted">
        Advance this complaint's workflow status. The change is recorded in the audit trail. Currently{' '}
        <span className="font-medium text-slate-300">{statusLabel(status)}</span>.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_OPTIONS.map((opt) => {
          const isCurrent = opt === status;
          const isSelected = opt === next;
          return (
            <button
              key={opt}
              type="button"
              disabled={mutation.isPending}
              onClick={() => {
                setNext(opt);
                mutation.reset();
              }}
              className={
                isSelected
                  ? 'rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40 disabled:opacity-50'
                  : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800 disabled:opacity-50'
              }
            >
              {statusLabel(opt)}
              {isCurrent ? ' (current)' : ''}
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        <label className="block text-[11px] text-muted">Note (optional — recorded in the audit trail)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. contacted rider / refunded / duplicate of #123"
          className="mt-1 input"
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="btn-primary"
        >
          {mutation.isPending ? 'Saving…' : dirty ? `Set to ${statusLabel(next)}` : 'No change'}
        </button>
        {mutation.isSuccess ? (
          <span className="text-xs text-ok">
            {mutation.data?.old_status === mutation.data?.new_status
              ? 'No change (already in that status).'
              : `Updated → ${mutation.data?.new_status_name}.`}
          </span>
        ) : null}
        {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
      </div>
    </Card>
  );
}
